import { useState } from "react";
import { format, startOfDay } from "date-fns";
import { DatePicker, Input, Tabs, TabsList, TabsTrigger } from "@racoon/ui";
import { Namespace, useDateFnsLocale, useT } from "@racoon/i18n";
import {
  atTime,
  changeFrom,
  changeTo,
  onDay,
  selectShortcut,
  selectedShortcutIndex,
  shortcutDays,
  shortcutLabel,
  type PickupWindow,
} from "../model/pickup-window";
import { acceptDigits, completeTime, formatTimeDigits, TIME_TEMPLATE } from "../model/time-input";

/**
 * An "HH:mm" box over a grey "00:00": typed digits fill the template from the left and the
 * rest stays grey. A full time applies at once; leaving early applies what the box shows.
 */
const TimeField = ({
  value,
  label,
  onCommit,
}: {
  value: Date;
  label: string;
  onCommit: (hours: number, minutes: number) => void;
}) => {
  const saved = format(value, "HHmm");
  // Digits typed but not applied yet; null shows the stored time. Every commit drops back to
  // null, so a time the window rules adjusted (clamped to now, kept after От) shows as stored.
  const [digits, setDigits] = useState<string | null>(null);

  const commit = (next: string) => {
    const time = completeTime(next);
    if (time && next !== saved) onCommit(time.hours, time.minutes);
    setDigits(null);
  };

  const draft = formatTimeDigits(digits ?? saved);

  return (
    <div className="relative">
      <Input
        type="text"
        size="sm"
        radius="lg"
        inputMode="numeric"
        maxLength={5}
        aria-label={label}
        // size="sm" brings md:text-xs, px-2.5 and the kit's border-input; the template
        // below has to start exactly where the typed text does, at every breakpoint
        className="!w-16 !border-border-primary bg-white !pl-3 !pr-0 md:!text-sm text-text-body"
        value={draft}
        onFocus={(event) => event.currentTarget.select()}
        onChange={(event) => {
          const next = acceptDigits(event.target.value);
          if (next === null) return;
          setDigits(next);
          if (next.length === 4) commit(next);
        }}
        onBlur={() => digits !== null && commit(digits)}
        onKeyDown={(event) => {
          if (event.key === "Enter") event.currentTarget.blur();
        }}
      />
      <span
        aria-hidden
        className="pointer-events-none absolute inset-y-0 left-px flex items-center pl-3 text-base md:text-sm"
      >
        <span className="invisible">{draft}</span>
        <span className="text-slate-400">{TIME_TEMPLATE.slice(draft.length)}</span>
      </span>
    </div>
  );
};

type PickupScheduleBlockProps = {
  value: PickupWindow;
  onChange: (window: PickupWindow) => void;
  /** The location's opening hours, "HH:mm" each — shown, never enforced. */
  availability?: { from: string; to: string };
  datePickerSide: "top" | "left";
};

/** "Запланировать вывоз": the job's service window, picked by day shortcut or exact От / До. */
export const PickupScheduleBlock = ({
  value,
  onChange,
  availability,
  datePickerSide,
}: PickupScheduleBlockProps) => {
  const t = useT();
  const locale = useDateFnsLocale();
  const days = shortcutDays(new Date());
  const selected = selectedShortcutIndex(value, days);

  const rows = [
    {
      label: t(Namespace.Common, "from"),
      date: value.from,
      minDate: startOfDay(new Date()),
      apply: (from: Date) => onChange(changeFrom(value, from, new Date())),
    },
    {
      label: t(Namespace.Common, "to"),
      date: value.to,
      minDate: startOfDay(value.from),
      apply: (to: Date) => onChange(changeTo(value, to, new Date())),
    },
  ];

  return (
    <div className="flex flex-col gap-4 rounded-xl bg-slate-100 p-4">
      <div className="flex flex-col gap-2">
        <p className="flex h-6 items-center text-base font-semibold leading-4 tracking-[-0.01em] text-text-body">
          {t(Namespace.Orders, "create.schedulePickup")}
        </p>
        {availability && (
          <p className="text-xs font-medium leading-4 tracking-[-0.01em] text-blue-600">
            {t(Namespace.Orders, "create.locationAvailability", availability)}
          </p>
        )}
      </div>

      {/* An empty value leaves every shortcut unselected while a custom date is picked */}
      <Tabs
        // Automatic activation selects a tab as it gets focus: tabbing into the row with no
        // shortcut matching would move the window to today. Click, Enter and Space still pick.
        activationMode="manual"
        value={selected === -1 ? "" : String(selected)}
        onValueChange={(index) => onChange(selectShortcut(value, days[Number(index)], new Date()))}
      >
        {/* `cn` is a plain join: the kit's list and trigger colours and boxes need `!` */}
        <TabsList className="w-full">
          {days.map((day, index) => (
            <TabsTrigger key={day.toISOString()} value={String(index)} className="flex-1">
              {shortcutLabel(day, locale)}
            </TabsTrigger>
          ))}
        </TabsList>
      </Tabs>

      <div className="flex flex-col gap-2">
        {rows.map((row) => (
          // The kit DatePicker names both triggers "Выберите дату"; the group says which end
          <div
            key={row.label}
            role="group"
            aria-label={row.label}
            className="flex h-8 items-center gap-2"
          >
            <span className="w-8 text-sm font-medium tracking-[-0.2px] text-text-subtle">
              {row.label}
            </span>
            <div className="ml-auto flex items-center gap-4">
              <DatePicker
                value={row.date}
                // `cn` is a plain join: the trigger Button's own px-4, text-sm and icon colour need `!`
                className="flex !h-8 w-[120px] items-center gap-2 rounded-lg border-border-primary bg-white !px-2 !py-1 !text-xs font-medium tracking-[-0.01em] text-text-body [&>svg]:!size-5 [&>svg]:!text-blue-600"
                minDate={row.minDate}
                side={datePickerSide}
                align="center"
                // Clicking the picked day again clears the picker; fresh references re-sync it
                // to the window instead, so the box never shows empty over a date still sent
                onChange={(day) =>
                  day
                    ? row.apply(onDay(day, row.date))
                    : onChange({ from: new Date(value.from), to: new Date(value.to) })
                }
              />
              <TimeField
                value={row.date}
                label={row.label}
                onCommit={(hours, minutes) => row.apply(atTime(row.date, hours, minutes))}
              />
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};
